<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses]"
  >
    <!-- Banner customizado de atualização (div arredondada) -->
    <div
      v-if="isRefresh && showUpdateBanner"
      class="update-banner-container"
      aria-live="polite"
    >
      <div class="update-banner shadow-sm">
        <div class="d-flex align-items-center">
          <feather-icon icon="InfoIcon" class="mr-1 text-primary"/>
          <span>Nova versão disponível. Atualize para uma melhor experiência.</span>
        </div>
        <div class="d-flex align-items-center ml-2">
          <b-button size="sm" variant="primary" class="mr-1" @click="update">
            Atualizar
          </b-button>
          <button
            type="button"
            class="btn btn-link btn-sm text-muted update-banner-close"
            aria-label="Fechar aviso de atualização"
            title="Fechar"
            @click="closeUpdateBanner"
          >
            &times;
          </button>
        </div>
      </div>
    </div>

    <Loader :visible="loading"/>
    <hotkeys :shortcuts="['H']" @triggered="showHelpModal"/>

    <component :is="layout">
      <router-view/>
    </component>

    <help-modal :show="showHelp" @close="closeModalHelp"/>
    <LoginReauthModal />
    <portal-target name="body-portal" multiple slim />

  </div>
</template>

<script>
// This will be populated in `beforeCreate` hook
import {$themeColors, $themeBreakpoints, $themeConfig} from '@themeConfig'
import {mapActions, mapState} from 'vuex'
import {ref, watch} from 'vue'
import Loader from '@/components/i2/http/loader/Loader.vue'
import useAppConfig from '@core/app-config/useAppConfig'

import {useWindowSize, useCssVar} from '@vueuse/core'

import store from '@/store'
import HelpModal from "@/components/i2/Help/HelpModal.vue";
import LoginReauthModal from '@/components/auth/LoginReauthModal.vue'

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {
    HelpModal,
    LoginReauthModal,

    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,
    Loader,
  },
  data: () => ({
    registration: null,
    isRefresh: false,
    refreshing: false,
    unsubscribe: null,
    logged: false,
    showUpdateBanner: false, // novo controle de exibição do banner
  }),
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    ...mapState('auth', ['isLogged']),
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
    ...mapState('loader', ['loading']),
  },
  created() {
    document.addEventListener('serviceWorkerUpdateEvent', this.appUpdateUI, {once: true})
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (this.refreshing) return
      this.refreshing = true
      window.location.reload()
    })
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const {isRTL} = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  async mounted() {
    this.installClarity()
    this.unsubscribe = this.$store.subscribe((mutation, state) => {
      if (mutation.type === 'auth/LOGIN') {
        window?.$chatwoot?.toggleBubbleVisibility("show")
        this.logged = state.auth.isLogged
      }
    })
    let token = window.localStorage.getItem('accessToken')
    if (!token) {
      return
    }
    if (window.location.pathname === '/pdv' || process.env.VUE_APP_ENV === 'development') {
      return;
    }
    let user = await this.me()
    let BASE_URL = "https://atendimento.eloja360.digital"
    let g = document.createElement('script')

    if (!window.$chatwoot && this.logged) {
      window.chatwootSettings = {"position": "left", "type": "standard", "launcherTitle": "Fale conosco no chat"}
      let s = document.getElementsByTagName("script")[0]
      g.src = BASE_URL + "/packs/js/sdk.js"
      g.defer = true
      g.async = true
      s.parentNode.insertBefore(g, s)
      g.onload = function () {
        window.chatwootSDK.run({
          websiteToken: '<CHATWOOT_WEBSITE_TOKEN>',
          baseUrl: BASE_URL,
          position: 'left'
        })
      }

      window.addEventListener("chatwoot:ready", function () {

        window.$chatwoot.setCustomAttributes({
          loja: user?.store?.name,
        })
        window.$chatwoot.setUser(user.uuid, {
          email: user.email,
          name: user.name,
          company_name: user?.store?.name,

        })
      })
      if (this.logged) {
        window?.$chatwoot?.toggleBubbleVisibility("show")
      }
    }
  },
  beforeDestroy() {
    window?.$chatwoot?.reset()
    window?.$chatwoot?.toggleBubbleVisibility("hide")
    this.unsubscribe()
  },
  methods: {
    ...mapActions('auth', ['me']),
    appUpdateUI(e) {
      this.registration = e.detail
      this.isRefresh = true
      this.showUpdateBanner = true // exibe o banner customizado
    },
    update() {
      this.isRefresh = false
      this.showUpdateBanner = false
      if (this.registration && this.registration.waiting) {
        this.registration.waiting.postMessage({type: 'SKIP_WAITING'})
      }
    },
    closeUpdateBanner() {
      this.showUpdateBanner = false
    },
    installClarity() {
      if (process.env.VUE_APP_ENV !== 'production') {
        return
      }
      const script = document.createElement('script');
      script.onload = () => {
        (function (c, l, a, r, i, t, y) {
          c[a] = c[a] || function () {
            (c[a].q = c[a].q || []).push(arguments)
          };
          t = l.createElement(r);
          t.async = 1;
          t.src = "https://www.clarity.ms/tag/" + i;
          y = l.getElementsByTagName(r)[0];
          y.parentNode.insertBefore(t, y);
        })(window, document, "clarity", "script", "<CLARITY_TAG_ID>");
      };
      document.head.appendChild(script);
    }
  },

  setup() {
    const showHelp = ref(false)

    const showHelpModal = () => {
      if (!showHelp.value) {
        showHelp.value = true
      }
    }

    const closeModalHelp = () => {
      showHelp.value = false
    }
    const {skin, skinClasses} = useAppConfig()

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const {width: windowWidth} = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
      showHelpModal,
      closeModalHelp,
      showHelp
    }
  },
}
</script>

<style scoped>
/* Container fixa no topo; centraliza o banner e não bloqueia a página */
.update-banner-container {
  position: fixed;
  top: 12px;
  left: 0;
  right: 0;
  z-index: 2050;
  display: flex;
  justify-content: center;
  pointer-events: none; /* não bloqueia cliques fora do banner */
  padding: 0 8px;
}

/* “Pílula” do banner */
.update-banner {
  max-width: 960px;
  width: 100%;
  background: #ffffff;
  color: #212529;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  pointer-events: auto; /* cliques funcionam dentro do banner */
  box-shadow: 0 4px 12px rgba(0,0,0,0.06);
}

/* Modo escuro (segue o tema já usado no app) */
.dark-layout .update-banner {
  background: #1f1f1f;
  color: #e6e6e6;
  border-color: #333;
  box-shadow: 0 4px 12px rgba(0,0,0,0.25);
}

/* Botão de fechar estilizado */
.update-banner-close {
  text-decoration: none;
  font-size: 20px;
  line-height: 1;
  padding: 0 6px;
}
</style>
